/**
 * Display Localization API Client with React Query Hooks
 *
 * Provides type-safe API client for Episode Display Localizations:
 * - Zod for runtime validation
 * - React Query for data fetching and caching
 * - Proper error handling and transformations
 *
 * Story 3.10: Episode Display Localizations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { apiGet, apiPost, apiPut, apiDelete } from 'services/api';
import logger from 'utils/logger';

// ============================================================================
// STEP 1: Define Zod Schemas
// ============================================================================

// Localized persona schema
const LocalizedPersonaSchema = z.object({
  name: z.string().min(1).max(100),
  description: z.string().min(1).max(500),
});

// Display localization response schema
const DisplayLocalizationResponseSchema = z.object({
  id: z.string().uuid(),
  episodeId: z.string().uuid(),
  languageCode: z.string().min(2).max(10),
  localizedName: z.string().max(200),
  localizedContext: z.string(),
  localizedGoals: z.array(z.string()),
  localizedPersonas: z.array(LocalizedPersonaSchema),
  createdAt: z.string(),
  updatedAt: z.string(),
});

// Display localization create schema
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const DisplayLocalizationCreateSchema = z.object({
  languageCode: z.string().min(2).max(10),
  localizedName: z.string().min(1).max(200),
  localizedContext: z.string().min(1),
  localizedGoals: z.array(z.string().min(1)).min(1),
  localizedPersonas: z.array(LocalizedPersonaSchema).min(1),
});

// Display localization update schema
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const DisplayLocalizationUpdateSchema = z.object({
  localizedName: z.string().min(1).max(200).optional(),
  localizedContext: z.string().min(1).optional(),
  localizedGoals: z.array(z.string().min(1)).min(1).optional(),
  localizedPersonas: z.array(LocalizedPersonaSchema).min(1).optional(),
});

// Localized dictionary entry schema
const LocalizedDictionaryEntrySchema = z.object({
  word: z.string().min(1),
  explanation: z.string().min(1),
  examplePhrases: z.array(z.string()),
});

// Generate request schema
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const GenerateRequestSchema = z.object({
  languageCode: z.string().min(2).max(10),
});

// Generate response schema (includes both generated content and metadata)
const GenerateResponseSchema = z.object({
  languageCode: z.string(),
  localizedName: z.string(),
  localizedContext: z.string(),
  localizedGoals: z.array(z.string()),
  localizedPersonas: z.array(LocalizedPersonaSchema),
  localizedDictionary: z.array(LocalizedDictionaryEntrySchema).default([]),
});

// Bulk generate request schema
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const BulkGenerateRequestSchema = z.object({
  targetLanguageCodes: z.array(z.string().min(2).max(10)).min(1),
});

// Bulk generate response schema
const BulkGenerateResponseSchema = z.object({
  localizations: z.record(z.string(), GenerateResponseSchema),
  successfulCount: z.number(),
  failedLanguages: z.array(z.string()),
});

// Export TypeScript types from Zod schemas
export type LocalizedPersona = z.infer<typeof LocalizedPersonaSchema>;
export type LocalizedDictionaryEntry = z.infer<typeof LocalizedDictionaryEntrySchema>;
export type DisplayLocalizationResponse = z.infer<typeof DisplayLocalizationResponseSchema>;
export type DisplayLocalizationCreate = z.infer<typeof DisplayLocalizationCreateSchema>;
export type DisplayLocalizationUpdate = z.infer<typeof DisplayLocalizationUpdateSchema>;
export type GenerateRequest = z.infer<typeof GenerateRequestSchema>;
export type GenerateResponse = z.infer<typeof GenerateResponseSchema>;
export type BulkGenerateRequest = z.infer<typeof BulkGenerateRequestSchema>;
export type BulkGenerateResponse = z.infer<typeof BulkGenerateResponseSchema>;

// ============================================================================
// STEP 2: Transform Functions (snake_case <-> camelCase)
// ============================================================================

/**
 * Transform snake_case API response to camelCase frontend types
 */
function transformSnakeToCamel<T>(obj: Record<string, unknown>): T {
  const transformed: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    // Convert snake_case to camelCase
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

    if (Array.isArray(value)) {
      transformed[camelKey] = value.map((item) =>
        typeof item === 'object' && item !== null
          ? transformSnakeToCamel(item as Record<string, unknown>)
          : item
      );
    } else if (typeof value === 'object' && value !== null) {
      transformed[camelKey] = transformSnakeToCamel(value as Record<string, unknown>);
    } else {
      transformed[camelKey] = value;
    }
  }

  return transformed as T;
}

/**
 * Transform camelCase frontend types to snake_case API request
 */
function transformCamelToSnake(obj: Record<string, unknown>): Record<string, unknown> {
  const transformed: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    // Convert camelCase to snake_case
    const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

    if (Array.isArray(value)) {
      transformed[snakeKey] = value.map((item) =>
        typeof item === 'object' && item !== null
          ? transformCamelToSnake(item as Record<string, unknown>)
          : item
      );
    } else if (typeof value === 'object' && value !== null) {
      transformed[snakeKey] = transformCamelToSnake(value as Record<string, unknown>);
    } else {
      transformed[snakeKey] = value;
    }
  }

  return transformed;
}

// ============================================================================
// STEP 3: Define Fetcher Functions
// ============================================================================

const fetchDisplayLocalizations = async (
  episodeId: string
): Promise<DisplayLocalizationResponse[]> => {
  const data = await apiGet<unknown[]>(`/api/admin/episodes/${episodeId}/display-localizations`);
  return z
    .array(DisplayLocalizationResponseSchema)
    .parse(data.map((item) => transformSnakeToCamel(item as Record<string, unknown>)));
};

const createDisplayLocalization = async (
  episodeId: string,
  localization: DisplayLocalizationCreate
): Promise<DisplayLocalizationResponse> => {
  const payload = transformCamelToSnake(localization as unknown as Record<string, unknown>);
  const data = await apiPost<unknown>(
    `/api/admin/episodes/${episodeId}/display-localizations`,
    payload
  );
  return DisplayLocalizationResponseSchema.parse(
    transformSnakeToCamel(data as Record<string, unknown>)
  );
};

const updateDisplayLocalization = async (
  episodeId: string,
  languageCode: string,
  update: DisplayLocalizationUpdate
): Promise<DisplayLocalizationResponse> => {
  const payload = transformCamelToSnake(update as unknown as Record<string, unknown>);
  const data = await apiPut<unknown>(
    `/api/admin/episodes/${episodeId}/display-localizations/${languageCode}`,
    payload
  );
  return DisplayLocalizationResponseSchema.parse(
    transformSnakeToCamel(data as Record<string, unknown>)
  );
};

const deleteDisplayLocalization = async (
  episodeId: string,
  languageCode: string
): Promise<void> => {
  await apiDelete<void>(`/api/admin/episodes/${episodeId}/display-localizations/${languageCode}`);
};

// 5 minute timeout for AI generation calls
const GENERATION_TIMEOUT_MS = 5 * 60 * 1000;

const generateDisplayLocalization = async (
  episodeId: string,
  languageCode: string
): Promise<GenerateResponse> => {
  const payload = transformCamelToSnake({
    targetLanguageCode: languageCode,
  } as unknown as Record<string, unknown>);
  const data = await apiPost<unknown>(
    `/api/admin/episodes/${episodeId}/display-localizations/generate`,
    payload,
    GENERATION_TIMEOUT_MS
  );
  return GenerateResponseSchema.parse(transformSnakeToCamel(data as Record<string, unknown>));
};

const generateBulkDisplayLocalizations = async (
  episodeId: string,
  languageCodes: string[]
): Promise<BulkGenerateResponse> => {
  const payload = transformCamelToSnake({ targetLanguageCodes: languageCodes } as unknown as Record<
    string,
    unknown
  >);
  const data = await apiPost<unknown>(
    `/api/admin/episodes/${episodeId}/display-localizations/generate-bulk`,
    payload,
    GENERATION_TIMEOUT_MS
  );
  return BulkGenerateResponseSchema.parse(transformSnakeToCamel(data as Record<string, unknown>));
};

// ============================================================================
// STEP 4: Define React Query Hooks
// ============================================================================

/**
 * Fetch all display localizations for an episode
 */
export const useDisplayLocalizations = (episodeId: string | undefined) => {
  return useQuery({
    queryKey: ['displayLocalizations', episodeId],
    queryFn: () => fetchDisplayLocalizations(episodeId!),
    enabled: !!episodeId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Create a new display localization
 */
export const useCreateDisplayLocalization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      episodeId,
      localization,
    }: {
      episodeId: string;
      localization: DisplayLocalizationCreate;
    }) => createDisplayLocalization(episodeId, localization),
    onSuccess: (_, { episodeId }) => {
      queryClient.invalidateQueries({ queryKey: ['displayLocalizations', episodeId] });
    },
    onError: (error) => {
      logger.error('Error creating display localization:', error);
    },
  });
};

/**
 * Update an existing display localization
 */
export const useUpdateDisplayLocalization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      episodeId,
      languageCode,
      update,
    }: {
      episodeId: string;
      languageCode: string;
      update: DisplayLocalizationUpdate;
    }) => updateDisplayLocalization(episodeId, languageCode, update),
    onSuccess: (_, { episodeId }) => {
      queryClient.invalidateQueries({ queryKey: ['displayLocalizations', episodeId] });
    },
    onError: (error) => {
      logger.error('Error updating display localization:', error);
    },
  });
};

/**
 * Delete a display localization
 */
export const useDeleteDisplayLocalization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ episodeId, languageCode }: { episodeId: string; languageCode: string }) =>
      deleteDisplayLocalization(episodeId, languageCode),
    onSuccess: (_, { episodeId }) => {
      queryClient.invalidateQueries({ queryKey: ['displayLocalizations', episodeId] });
    },
    onError: (error) => {
      logger.error('Error deleting display localization:', error);
    },
  });
};

/**
 * Generate a display localization using AI
 */
export const useGenerateDisplayLocalization = () => {
  return useMutation({
    mutationFn: ({ episodeId, languageCode }: { episodeId: string; languageCode: string }) =>
      generateDisplayLocalization(episodeId, languageCode),
    onError: (error) => {
      logger.error('Error generating display localization:', error);
    },
  });
};

/**
 * Generate display localizations for multiple languages using AI (single LLM call)
 */
export const useGenerateBulkDisplayLocalizations = () => {
  return useMutation({
    mutationFn: ({ episodeId, languageCodes }: { episodeId: string; languageCodes: string[] }) =>
      generateBulkDisplayLocalizations(episodeId, languageCodes),
    onError: (error) => {
      logger.error('Error generating bulk display localizations:', error);
    },
  });
};
